/**
 * Cart Recommendations - "Komplettera din hudvårdsrutin"
 * Fetches product recommendations from Shopify's Search & Discovery API
 * based on items currently in the cart, and renders them in the sidebar cart.
 */
(()=>{
  'use strict';

  class GlobalCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.maxProducts = 6;
    }

    async connectedCallback() {
      try {
        const template = this.querySelector(':scope > template');
        if (template) {
          this.settings = JSON.parse(template.innerHTML);
          template.remove();
        }
      } catch(e) {
        console.log(e);
        return;
      }

      this.GlobalCart = await new GlobalCart();
      this.mountStyles();
      this.GlobalCart.subscribe(() => this.render());
      this.render();
    }

    mountStyles() {
      if (window.__cartRecsStylesMounted) return;
      window.__cartRecsStylesMounted = true;

      const styles = document.createElement('style');
      styles.textContent = `
        global-cart-recommendations {
          display: block;
          padding-top: 6px;
        }

        .cart-recs__wrapper {
          border-top: 1px solid #e4e4e4;
          position: relative;
          background: #fff;
          padding: 0 16px;
        }

        .cart-recs__heading {
          text-align: center;
          font-size: 18px;
          font-weight: 700;
          font-family: var(--font-accent, "ridley_groteskmedium", sans-serif);
          padding: 10px 0 8px;
          margin: 0;
        }

        .cart-recs__grid img,
        .cart-recs__grid a {
          -webkit-user-drag: none;
          user-select: none;
          pointer-events: auto;
        }

        .cart-recs__grid a {
          touch-action: pan-x;
        }

        .cart-rec-thumb {
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
        }

        .cart-rec-thumb__image {
          width: 100%;
          display: block;
          border-radius: 5px;
          margin-bottom: 4px;
        }

        .cart-rec-thumb__title {
          font-size: 10px;
          text-transform: uppercase;
          line-height: 1.2;
          font-weight: 700;
          letter-spacing: 0.3px;
          color: #000;
          text-decoration: none;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.4em;
        }

        .cart-rec-thumb__price {
          font-size: 11px;
          font-weight: 500;
          margin-bottom: 4px;
          text-align: left;
        }

        .cart-rec-thumb__bottom {
          margin-top: auto;
        }

        .cart-rec-thumb__price s {
          color: #999;
          margin-right: 4px;
        }

        .cart-rec-thumb select {
          width: 100%;
          margin-bottom: 6px;
          padding: 6px 8px;
          font-size: 11px;
          background-size: 12px;
          border: 1px solid #c7c7c7;
        }

        .cart-rec-thumb__action {
          height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 0;
          color: #fff;
          background: #000;
          border: 1px solid #000;
          font-size: 10px;
          width: 100%;
          cursor: pointer;
          padding: 0 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
          margin-top: auto;
          border-radius: 5px;
        }

        .cart-rec-thumb__action:hover {
          background: #333;
          border-color: #333;
        }

        .cart-rec-thumb__action.btn--loading {
          opacity: 0.6;
          pointer-events: none;
        }

        .cart-rec-thumb__action.btn--added {
          background: #333;
        }

        .cart-recs__grid {
          position: relative;
        }

        .cart-recs__grid .nice-scroll__content .arrow {
          background: rgba(255, 255, 255, 0.85);
          border: 1px solid #000;
          border-radius: 0;
          height: 28px;
          width: 28px;
          top: 20%;
          z-index: 10;
        }

        .cart-recs__grid .nice-scroll__content .arrow:hover {
          background: rgba(255, 255, 255, 1);
        }

        .cart-recs__grid .nice-scroll__content .arrow:before {
          font-size: 13px;
          color: #000;
          opacity: 1;
        }

        .cart-recs__grid .nice-scroll__content .arrow--left {
          left: 20px;
          transform: translate(0, -50%);
        }

        .cart-recs__grid .nice-scroll__content .arrow--right {
          right: 20px;
          transform: translate(0, -50%);
        }

        /* Hide left arrow initially until user scrolls right */
        .cart-recs__grid .arrow--left.arrow--hidden {
          display: none !important;
        }

        /* Hide all arrows on mobile */
        @media (max-width: 768px) {
          .cart-recs__grid .arrow {
            display: none !important;
          }
        }

        .global-cart__adding global-cart-recommendations {
          pointer-events: none;
        }
      `;
      document.head.appendChild(styles);
    }

    async fetchRecommendations() {
      const cartItems = this.GlobalCart.cart?.items || [];
      if (!cartItems.length) return [];

      const cartProductIds = new Set(cartItems.map(i => i.product_id));
      const seenProductIds = new Set();
      const allRecs = [];
      const route = window.Shopify?.routes?.root || '/';

      // Fetch from all unique cart products, requesting more than we need
      const uniqueProductIds = [...cartProductIds];
      const fetches = uniqueProductIds.slice(0, 4).map(async (productId) => {
        try {
          const r = await fetch(
            `${route}recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`
          );
          const data = await r.json();
          return data.products || [];
        } catch(e) {
          return [];
        }
      });

      const results = await Promise.all(fetches);
      for (const products of results) {
        for (const product of products) {
          if (cartProductIds.has(product.id) || seenProductIds.has(product.id)) continue;
          seenProductIds.add(product.id);
          allRecs.push(product);
          if (allRecs.length >= this.maxProducts) break;
        }
        if (allRecs.length >= this.maxProducts) break;
      }

      // If complementary didn't yield enough, try related for each cart product
      if (allRecs.length < this.maxProducts) {
        for (const productId of uniqueProductIds) {
          if (allRecs.length >= this.maxProducts) break;
          try {
            const r = await fetch(
              `${route}recommendations/products.json?product_id=${productId}&limit=10&intent=related`
            );
            const data = await r.json();
            for (const product of (data.products || [])) {
              if (cartProductIds.has(product.id) || seenProductIds.has(product.id)) continue;
              seenProductIds.add(product.id);
              allRecs.push(product);
              if (allRecs.length >= this.maxProducts) break;
            }
          } catch(e) {}
        }
      }

      return allRecs;
    }

    async render() {
      // Prevent concurrent renders — queue the latest and skip intermediate ones
      this._renderQueued = true;
      if (this._rendering) return;
      this._rendering = true;
      this._renderQueued = false;

      try {
        await this._doRender();
      } catch(e) {
        console.log('Cart recommendations render error:', e);
      }

      this._rendering = false;

      // If another render was requested while we were busy, run it now
      if (this._renderQueued) {
        this._renderQueued = false;
        this.render();
      }
    }

    async _doRender() {
      const cartItems = this.GlobalCart.cart?.items || [];
      if (!cartItems.length) {
        this.innerHTML = '';
        this.products = [];
        return;
      }

      const products = await this.fetchRecommendations();
      if (!products.length) {
        // Keep existing recommendations visible if we had some before
        if (this.products?.length) return;
        this.innerHTML = '';
        return;
      }

      this.products = products;
      const heading = this.settings?.heading || 'Komplettera din hudvårdsrutin';

      this.innerHTML = `
        <div class="cart-recs__wrapper">
          <p class="cart-recs__heading">${heading}</p>
          <div class="cart-recs__grid">
            <nice-scroll
              class="block-12/12"
              slideTarget=".cart-rec-thumb"
              showControls
              hideScrollBar>
              <div class="flex">
                ${products.map(product => `
                  <div class="cart-rec-thumb">
                    ${this.renderThumb(product)}
                  </div>
                `).join('')}
              </div>
            </nice-scroll>
          </div>
        </div>
      `;

      this.bindEvents();
    }

    renderThumb(product) {
      const title = product.title || '';
      const image = product.featured_image
        ? (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image)
        : '';
      const imgSrc = image ? `${image}&width=256` : '';
      const variants = product.variants || [];
      const firstVariant = variants[0] || {};
      const hasMultipleVariants = variants.length > 1;

      const priceHtml = this.formatPriceHtml(firstVariant);
      const btnLabel = this.settings?.translations?.add_to_cart || 'Lägg till';

      return `
        <form class="block-fh flex column-wrap"
              action="/cart/add" method="post" enctype="multipart/form-data"
              data-product-id="${product.id}"
              style="display:flex;flex-direction:column;">
          <a href="/products/${product.handle}" class="d-block">
            <img class="cart-rec-thumb__image" src="${imgSrc}" alt="${title}" loading="lazy" width="256" height="256" draggable="false">
          </a>
          <a href="/products/${product.handle}" class="cart-rec-thumb__title">${title}</a>
          <div class="cart-rec-thumb__bottom">
            <div class="cart-rec-thumb__price js-rec-price">${priceHtml}</div>
            ${hasMultipleVariants ? `
              <select name="variant_select" title="variant selector" class="js-rec-variant-select">
                ${variants.map(v => `
                  <option value="${v.id}"
                    data-price="${v.price}"
                    data-compare-price="${v.compare_at_price || ''}">
                    ${v.title === 'Default Title' ? title : v.title}
                  </option>
                `).join('')}
              </select>
            ` : ''}
            <input type="hidden" name="quantity" value="1"
                   data-variant-id="${firstVariant.id}"
                   data-variant-inventory-qty="1" min="1"/>
            <input type="hidden" name="id" value="${firstVariant.id}" />
            <button type="button"
                    class="cart-rec-thumb__action"
                    data-variant-id="${firstVariant.id}"
                    onClick="GlobalCartMain.addProductFromButton(event)">
              <span class="text">${btnLabel}</span>
            </button>
          </div>
        </form>
      `;
    }

    formatPriceHtml(variant) {
      if (!variant || !variant.price) return '';
      const price = this.GlobalCart.ShopifyUtils.formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const comparePrice = this.GlobalCart.ShopifyUtils.formatMoney(variant.compare_at_price);
        return `<s>${comparePrice}</s> ${price}`;
      }
      return price;
    }

    bindEvents() {
      // Variant selects — update hidden id input, price, and button variant
      this.querySelectorAll('.js-rec-variant-select').forEach(select => {
        select.addEventListener('change', () => {
          const form = select.closest('form');
          const idInput = form.querySelector('[name="id"]');
          const btn = form.querySelector('.js-rec-atb');
          const priceEl = form.querySelector('.js-rec-price');
          const option = select.options[select.selectedIndex];

          idInput.value = select.value;
          btn.dataset.variantId = select.value;

          const price = parseInt(option.dataset.price);
          const comparePrice = option.dataset.comparePrice ? parseInt(option.dataset.comparePrice) : null;
          const variant = { price, compare_at_price: comparePrice };
          priceEl.innerHTML = this.formatPriceHtml(variant);
        });
      });

      // Resize thumbs for nice-scroll and hook into sidebar open
      const sidebar = document.querySelector('.js-sidebar-cart');
      if (sidebar) {
        const sub = sidebar.subscribe(({open}) => {
          if (open) {
            sidebar.ObserverLite.unsubscribe(sub);
            this.resizeThumbs();
          }
        });
      }
      this.resizeThumbs();
      this.initDragScroll();
      this.initArrowVisibility();

    }

    initDragScroll() {
      const scrollEl = this.querySelector('[data-nicescroll-content]');
      if (!scrollEl) return;

      let isDown = false;
      let startX;
      let scrollLeft;
      let hasDragged = false;

      scrollEl.style.cursor = 'grab';

      scrollEl.addEventListener('mousedown', (e) => {
        if (e.target.closest('button, select')) return;
        isDown = true;
        hasDragged = false;
        startX = e.pageX - scrollEl.offsetLeft;
        scrollLeft = scrollEl.scrollLeft;
        scrollEl.style.cursor = 'grabbing';
      });

      scrollEl.addEventListener('mouseleave', () => {
        isDown = false;
        scrollEl.style.cursor = 'grab';
      });

      scrollEl.addEventListener('mouseup', () => {
        isDown = false;
        scrollEl.style.cursor = 'grab';
      });

      scrollEl.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - scrollEl.offsetLeft;
        const walk = x - startX;
        if (Math.abs(walk) > 5) hasDragged = true;
        scrollEl.scrollLeft = scrollLeft - walk;
      });

      // Prevent clicks on links after dragging
      scrollEl.addEventListener('click', (e) => {
        if (hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          hasDragged = false;
        }
      }, true);
    }

    initArrowVisibility() {
      // nice-scroll creates arrows asynchronously, so observe for them
      const setup = () => {
        const scrollEl = this.querySelector('[data-nicescroll-content]');
        const leftArrow = this.querySelector('.arrow--left');
        if (!scrollEl || !leftArrow) return false;

        leftArrow.classList.add('arrow--hidden');

        scrollEl.addEventListener('scroll', () => {
          if (scrollEl.scrollLeft > 5) {
            leftArrow.classList.remove('arrow--hidden');
          } else {
            leftArrow.classList.add('arrow--hidden');
          }
        });
        return true;
      };

      if (!setup()) {
        const obs = new MutationObserver(() => {
          if (setup()) obs.disconnect();
        });
        obs.observe(this, { childList: true, subtree: true });
      }
    }

    resizeThumbs() {
      const sidebar = this.closest('global-sidebar-cart-ui');
      const containerWidth = sidebar ? sidebar.offsetWidth : 400;
      const padding = 32; // 16px padding on each side
      const availableWidth = containerWidth - padding;
      const thumbs = this.querySelectorAll('.cart-rec-thumb');
      const count = thumbs.length;
      const spaceBetween = 8;
      // If 3 or fewer, fit them evenly; if 4+, show 3 full + peek of 4th
      const itemsInView = count <= 3 ? count : 3.5;
      thumbs.forEach(thumb => {
        thumb.style.width = (availableWidth / itemsInView - spaceBetween) + 'px';
        thumb.style.marginRight = spaceBetween + 'px';
      });
    }
  }

  customElements.define('global-cart-recommendations', GlobalCartRecommendations);
})();
